import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type {
  ICallingTriggerActionType,
  IResponseFullAICallingAgent,
} from 'entities/AICalling/api/types'
import type { MergeField } from 'entities/MergeField/model/MergeField'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { type RequestAttachment } from 'entities/Attachment/model/RequestAttachment'
import {
  EmojiAction,
  EnumVariantMessageField,
  makeSavedReplyInputAction,
  mergeFieldReplacer,
  MessageFieldStore,
  SavedRepliesAction,
  ShortenLinkAction,
} from 'widgets/MessageField'
import { MergeFieldsIconAction } from 'widgets/MergeField/ui/actions/MergeFieldsIconAction/MergeFieldsIconAction'
import { MediaAction } from 'widgets/MessageField/ui/FieldActions/MediaAction/MediaAction'
import { SavedRepliesStore } from 'widgets/SavedReplies'
import { AfterCallModalContent } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/Actions/ui/AfterCallModalContent'
import { AfterCallAction } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/Actions/model/AfterCallAction'
import { DuringCallAction } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/Actions/model/DuringCallAction'
import { createCallingAction } from '../model/createCallingAction'
import { DuringCallModalContent } from '../ui/DuringCallModalContent'

export class ActionsStore {
  readonly messageFieldStore: MessageFieldStore
  private readonly _savedRepliesStore: SavedRepliesStore
  private _addTriggerType: ICallingTriggerActionType | null = null
  cards: Array<AfterCallAction | DuringCallAction> = []
  name = ''
  triggerDescription = ''
  voiceResponse = ''

  constructor(
    readonly entities: IEntitiesStore,
    readonly response: IResponseFullAICallingAgent | null
  ) {
    this._savedRepliesStore = new SavedRepliesStore(this.entities)
    this.messageFieldStore = new MessageFieldStore(
      {
        placeholder: 'Write your message...',
        styles: {
          minHeight: 102,
          maxHeight: 200,
        },
        variant: EnumVariantMessageField.Default,
        showActionsItems: 5,
        resetOnUnmount: false,
        makeInputActions: (store) => [makeSavedReplyInputAction(store, this._savedRepliesStore)],
        makeActions: () => [
          { iconButtonComponent: <MediaAction /> },
          { iconButtonComponent: <SavedRepliesAction store={this._savedRepliesStore} useHotKey /> },
          { iconButtonComponent: <EmojiAction /> },
          {
            iconButtonComponent: (
              <MergeFieldsIconAction
                onAddMergeField={(value) => this._onAddMergeField(value)}
                isAllIntegration
              />
            ),
          },
          { iconButtonComponent: <ShortenLinkAction /> },
        ],
      },
      this.entities
    )
    this.syncFromResponse(this.response)
    makeAutoObservable(this)
  }

  get cardsPayload() {
    return this.cards.map((card) => card.payload)
  }

  syncFromResponse = (response: IResponseFullAICallingAgent | null) => {
    if (response) {
      this.cards = response.actions.map((action) => createCallingAction(action))
    }
  }

  getCardsByTrigger = (triggerType: ICallingTriggerActionType) => {
    return this.cards.filter((card) => card.triggerType === triggerType)
  }

  openAddModal = (section: ICallingTriggerActionType) => {
    this._addTriggerType = section
    this._openModal()
  }

  editAction = (actionId: string, section: ICallingTriggerActionType) => {
    this._addTriggerType = section
    this._openModal(actionId)
  }

  removeAction = (clientId: string) => {
    this.cards = this.cards.filter((card) => card.clientId !== clientId)
  }

  onCloseModal = (modalId: string) => {
    this._addTriggerType = null
    modalStore.closeModal(modalId)
    this.messageFieldStore.clear()
    this.resetDuringCallForm()
  }

  createAction = (message: string, mediaUrl: RequestAttachment[]) => {
    let card: AfterCallAction | DuringCallAction

    if (this._addTriggerType === 'during_call') {
      card = new DuringCallAction({
        action_type: 'send_message',
        trigger_type: 'during_call',
        settings: {
          message,
          media_url: mediaUrl,
          name: this.name,
          trigger_description: this.triggerDescription,
          voice_response: this.voiceResponse || undefined,
        },
      })
    } else {
      card = new AfterCallAction({
        action_type: 'send_message',
        trigger_type: 'end_of_call',
        settings: {
          message,
          media_url: mediaUrl,
        },
      })
    }

    this.cards = [...this.cards, card]
  }

  onSave = (modalId: string, editingId: string | null) => {
    const message = this.messageFieldStore.text
    const mediaUrl = this.messageFieldStore.media_url

    if (editingId) {
      const action = this.cards.find((card) => card.clientId === editingId)
      if (action instanceof DuringCallAction) {
        action.actionName = this.name
        action.description = this.triggerDescription
        action.voiceResponse = this.voiceResponse || undefined
      }
      action?.syncMessage(message, mediaUrl)
    } else {
      this.createAction(message, mediaUrl)
    }

    this.onCloseModal(modalId)
  }

  onDelete = (modalId: string, editingId: string) => {
    this.removeAction(editingId)
    this.onCloseModal(modalId)
  }

  private _openModal = (editingActionId?: string) => {
    const modalId = nanoid()
    const editingId = editingActionId ?? null

    this.messageFieldStore.reset()
    this.resetDuringCallForm()

    const action = this.cards.find((card) => card.clientId === editingId)

    if (action) {
      this.messageFieldStore.setMessageData({
        message: action.settings.message,
        attachments: action.media_url.map((media) => new Attachment({ requestAttachment: media })),
        isFocus: false,
        isReplace: true,
        isReset: false,
      })
      if (action instanceof DuringCallAction) {
        this.name = action.actionName
        this.triggerDescription = action.description
        this.voiceResponse = action.voiceResponse || ''
      }
    }

    modalStore.addModal({
      id: modalId,
      width: 480,
      pureContent: true,
      title: editingId ? 'Edit action' : 'Add action',
      ModalContentProps: {
        store: this,
        modalId,
        editingId,
      },
      ModalContent:
        this._addTriggerType === 'during_call' ? DuringCallModalContent : AfterCallModalContent,
    })
  }

  setName = (value: string) => {
    this.name = value
  }

  setTriggerDescription = (value: string) => {
    this.triggerDescription = value
  }

  setVoiceResponse = (value: string) => {
    this.voiceResponse = value
  }

  resetDuringCallForm = () => {
    this.name = ''
    this.triggerDescription = ''
    this.voiceResponse = ''
  }

  private _onAddMergeField = (field: MergeField) => {
    if (!this.messageFieldStore.addContent) return
    const fieldContent = mergeFieldReplacer(this.entities, field.template) + '&nbsp;'
    this.messageFieldStore.addContent(fieldContent)
  }
}
